import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useToast } from '../../components/ds';
import { Button, Table, Badge, Spinner } from '../../components/ds';
import AddCompanyModal from './AddCompanyModal';
import FeaturesModal, { TARGETING_OPTIONS } from './FeaturesModal';
import './AccountDetailPage.css';

export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [account, setAccount] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [features, setFeatures] = useState(null);
  const [disabling, setDisabling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [accounts, usersData, companiesData, featuresData] = await Promise.all([
        api.getAccounts(),
        api.getUsers(id),
        api.getAccountCompanies(id),
        api.getAccountFeatures(id),
      ]);

      const acct = (accounts || []).find((a) => a.uuid === id);
      setAccount(acct || null);
      setUsers(usersData || []);
      setFeatures(featuresData?.features || null);

      // Enrich companies with detail + applications
      const enriched = await Promise.all(
        (companiesData || []).map(async (c) => {
          try {
            const [detail, apps] = await Promise.all([
              api.getCompanyDetail(c.company_id),
              api.getApplications(c.company_id),
            ]);
            return {
              ...c,
              tax_id_number: detail?.tax_id_number || '',
              address: detail?.address || '',
              applications: apps || [],
            };
          } catch {
            return { ...c, applications: [] };
          }
        })
      );
      setCompanies(enriched);
    } catch (err) {
      addToast('Failed to load account details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable this account?')) return;
    setDisabling(true);
    try {
      await api.disableAccount(id);
      addToast('Account disabled', 'success');
      fetchData();
    } catch (err) {
      addToast('Failed to disable account: ' + err.message);
    } finally {
      setDisabling(false);
    }
  };

  const handleRemoveCompany = async (companyId, companyName) => {
    if (!window.confirm(`移除「${companyName}」？此操作會同時移除 PAC 委託關係。`)) return;
    try {
      await api.removeAccountCompany(id, companyId);
      addToast('Company removed', 'success');
      fetchData();
    } catch (err) {
      addToast('Failed to remove company: ' + err.message);
    }
  };

  const handleCompanyAdded = () => {
    setShowAddCompany(false);
    addToast('Company added', 'success');
    fetchData();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatTimezone = (tz) => {
    if (!tz) return '-';
    try {
      const parsed = JSON.parse(tz);
      return parsed.name || tz;
    } catch {
      return tz;
    }
  };

  if (loading) {
    return <div className="loading"><Spinner /></div>;
  }

  if (!account) {
    return <div className="loading">Account not found</div>;
  }

  return (
    <div>
      {/* Back Button */}
      <Button variant="secondary" onClick={() => navigate('/accounts')} style={{ marginBottom: 16 }}>
        ← Back to Accounts
      </Button>

      {/* Account Info */}
      <div className="detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div className="detail-info">
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>{account.account_name}</h1>
          <div className="detail-meta" style={{ display: 'flex', gap: 16, marginTop: 8, color: 'var(--text-secondary)', fontSize: 14 }}>
            <span>Timezone: {formatTimezone(account.timezone)}</span>
            <span>
              Status: <Badge variant={account.account_status === 'active' ? 'primary' : 'default'}>{account.account_status}</Badge>
            </span>
          </div>
        </div>
        {account.account_status === 'active' && (
          <Button
            variant="secondary"
            style={{ color: 'var(--status-error-text)' }}
            onClick={handleDisable}
            disabled={disabling}
          >
            {disabling ? 'Disabling...' : 'Disable Account'}
          </Button>
        )}
      </div>

      {/* Users */}
      <div className="detail-section" style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 16 }}>Users</h2>
        <Table
          columns={[
            { key: 'user_name', title: 'User Name' },
            { key: 'email', title: 'Email' },
            { key: 'role', title: 'Role', render: (_, row) => (row.account_user_attribute?.role_ids || row.role_ids || []).join(', ') },
            { key: 'CreatedAt', title: 'Created', render: (val) => formatDate(val) },
          ]}
          data={users}
          rowKey="uuid"
          emptyText="No users"
        />
      </div>

      {/* Feature Settings */}
      <div className="detail-section" style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2>Feature Settings</h2>
          <Button variant="secondary" onClick={() => setShowFeaturesModal(true)}>Edit</Button>
        </div>
        <div style={{ padding: 20, background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
          <h3 style={{ marginBottom: 12, fontSize: 15, color: 'var(--text-secondary)' }}>
            Targeting
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {TARGETING_OPTIONS.map((opt) => (
              <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  checked={features?.targeting?.[opt.key] || false}
                  disabled
                  style={{ width: 18, height: 18 }}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Companies */}
      <div className="detail-section" style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2>Companies</h2>
          <Button onClick={() => setShowAddCompany(true)}>+ Add Company</Button>
        </div>

        {companies.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32, background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            No companies associated
          </div>
        ) : (
          companies.map((company) => (
            <div key={company.company_id} style={{ padding: 20, marginBottom: 12, background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div>
                  <h3 style={{ margin: 0 }}>{company.company_name}</h3>
                  <code style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{company.company_id}</code>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ color: 'var(--status-error-text)' }}
                  onClick={() => handleRemoveCompany(company.company_id, company.company_name)}
                >
                  Remove
                </Button>
              </div>
              {(company.tax_id_number || company.address) && (
                <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  {company.tax_id_number && <span>Tax ID: {company.tax_id_number}</span>}
                  {company.address && <span>Address: {company.address}</span>}
                </div>
              )}
              {company.applications && company.applications.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <h4 style={{ fontSize: 13, marginBottom: 4, color: 'var(--text-secondary)' }}>Applications</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {company.applications.map((app) => (
                      <Badge key={app.uuid} variant="default">
                        {app.application_name} ({app.platform})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showAddCompany && (
        <AddCompanyModal
          accountId={id}
          onClose={() => setShowAddCompany(false)}
          onAdded={handleCompanyAdded}
        />
      )}

      {showFeaturesModal && (
        <FeaturesModal
          accountUuid={id}
          features={features}
          onClose={() => setShowFeaturesModal(false)}
          onSaved={(updatedFeatures) => {
            setFeatures(updatedFeatures);
            setShowFeaturesModal(false);
            addToast('Feature settings updated', 'success');
          }}
        />
      )}
    </div>
  );
}
